<script setup lang="ts">
import { computed, shallowRef, watch } from "vue"

import { ICON } from "@/lib/icons"
import type { ItemSummary, SelectOption } from "@/types/models"

type AvailableItem = ItemSummary & { is_container: boolean }

const props = defineProps<{
  items: AvailableItem[]
  processing: boolean
}>()

const emit = defineEmits<{
  add: [itemId: string, includeContents: boolean]
}>()

const selectedItemId = shallowRef<string>()
const includeContents = shallowRef(false)
const selectedItem = computed(() => props.items.find((item) => item.id === selectedItemId.value))
const options = computed<SelectOption[]>(() =>
  props.items.map((item) => ({
    label: item.location_path ? `${item.name} · ${item.location_path}` : item.name,
    value: item.id,
  })),
)

watch(selectedItem, (item) => {
  includeContents.value = item?.is_container ?? false
})

function submit() {
  if (!selectedItemId.value) return

  emit(
    "add",
    selectedItemId.value,
    Boolean(selectedItem.value?.is_container && includeContents.value),
  )
  selectedItemId.value = undefined
}
</script>

<template>
  <form class="flex flex-col gap-2" @submit.prevent="submit">
    <div class="flex items-end gap-2">
      <UFormField label="Add an item" class="min-w-0 flex-1">
        <USelectMenu
          v-model="selectedItemId"
          :items="options"
          value-key="value"
          placeholder="Search available items..."
          class="w-full"
        />
      </UFormField>
      <UButton
        type="submit"
        label="Add"
        :icon="ICON.add"
        :loading="processing"
        :disabled="!selectedItemId"
      />
    </div>
    <UCheckbox
      v-if="selectedItem?.is_container"
      v-model="includeContents"
      label="Add all contents"
    />
  </form>
</template>
